/**
 * A collection of mostly navigation-related methods for use by SurveyForm
 */

import * as _ from 'lodash'
import Utils from '@/providers/Utils'

export default {
    computed: {
        disableNext () {
            return this.questions && this.survey.current_question >= this.questions.length - 1
        },
        disablePrevious () {
            return this.survey.current_question <= 0 || this.survey.questionnaire_code == 'DIETID'
        },
    },
    methods: {
        getEnterDataOptions (question) {
            const options = question.options
            return _.map(options, (opt, index) => {
                const translatedOpt = this.$i18n.translateQuestionField(question, `OPTIONS_${index}`)
                const i = translatedOpt.indexOf('__')
                return {
                    prefix: translatedOpt.substring(0, i),
                    suffix: translatedOpt.substring(i + 2)
                }
            })
        },
        // generate an 'options' object for a b-form-radio-group
        getRadioOptions (question) {
            const options = question.options
            return _.map(options, (opt, index) => {
                return {
                    text: this.$i18n.translateQuestionField(question, `OPTIONS_${index}`),
                    value: 'r_' + index
                }
            })
        },
        // generate an 'options' object for a multi-select-one b-form-radio-group
        getMultiRadioOptions (question, i, start, stop) {
            const options = question.multi_options[i]
            if (question.multi_options[i][0] !== 'N/A') {
                let response = []
                switch (start){
                    case 0: return null
                    case 1 && 2:
                        for (let j = start + 1; j <= stop + 1; j++) {
                            response.push ({
                                text: this.$i18n.gt(`QUESTION_RTC_RTC_OPTION_${j}`),
                                value: 'r_' + (j)
                            })
                        }
                        return response
                }
            }
            if (start == null || stop == null) {
                return _.map(options, (opt, index) => {
                    return {
                        text: this.$i18n.gt(`QUESTION_RTC_RTC_OPTION_${index}`),
                        value: 'r_' + index
                    }
                })
            } else {
                let response = []
                for (let j = start; j <= stop; j++) {
                    response.push ({
                        text: this.$i18n.gt(`QUESTION_RTC_RTC_OPTION_${j}`),
                        value: 'r_' + j
                    })
                }
                return response
            }
        },
        // generate an 'options' object for a b-form-checkbox-group
        getCheckboxOptions (question) {
            const options = question.options
            return _.map(options, (opt, index) => {
                return {
                    text: this.$i18n.translateQuestionField(question, `OPTIONS_${index}`),
                    value: 'r_' + index
                }
            })
        },

        getQuestionIndexWidth () {
            return { width: `calc(100% / ${this.getApplicableQuestions().length})` }
        },

        previousQuestion (e) {
            var prev = this.getPreviousApplicableQuestionIndex()
            if (prev != -1) {
                this.survey.current_question = prev
                this.updateQuestionAndSection()
            }
        },
        nextQuestion (e) {
            this.validateQuestion()
            if (this.validation.valid) {
                var next = this.getNextApplicableQuestionIndex()
                if (next != -1) {
                    this.survey.current_question = next
                    this.updateQuestionAndSection()
                }
            }
        },
        goToSection (s) {
            const question = _.find(this.getApplicableQuestions(), (q) => {
                return s.question_names.indexOf(q.name) != -1
            })
            this.survey.current_question = question.index
            this.updateQuestionAndSection()
        },
        goToQuestion (q) {
            if (this.survey.questionnaire_code != 'DIETID') { // no random navigation for DIETID
                this.survey.current_question = _.findIndex(this.questions, q)
                this.updateQuestionAndSection()
            }
        },
        goToQuestionByName (qname) {
            const question = _.find(this.questions, (q) => q.name == qname)
            this.survey.current_question = _.findIndex(this.questions, question)
            this.updateQuestionAndSection()
        },

        updateQuestionAndSection () {
            this.autosave()
            this.validation = { valid: true, warned: false, message: null }
            // focus first input (only for ENTER_DATA)
            this.$nextTick(() => {
                var inputs = this.$refs.input0
                if (inputs && inputs.length > 0 && inputs[0]) {
                    inputs[0].$el.focus()
                }
            })
        },
        getApplicableQuestions () {
            return _.filter(this.questions, (q) => {
                return this.isApplicableQuestion(q)
            })
        },
        isApplicableQuestion (question) {
            if (question.type == 'DIETID') {
                return this.isApplicableDietidQuestion(question) // see SurveyFormDietidMixin
            } else {
                if (question.showif) {
                    const has = question.showif.replace(/hasResponse/g, 'this.hasResponse')
                    const isApplicable = eval(has)
                    if (question.response) {
                        question.response.is_na = !isApplicable
                    }
                    return isApplicable
                } else {
                    return true
                }
            }
        },

        // used to evaluate the 'showif' attribute for conditional questions - sample format:
        // [this.]hasResponse('AUDIT_HOW_OFTEN', 'r_1,r_2,r_3,r_4') &&
        //   ([this.]hasResponse('AUDIT_NUMBER', 'r_1,r_2,r_3,r_4') || ...)
        hasResponse (qname, val) {
            var question = _.find(this.questions, (q) => {
                return q.name == qname
            })
            var ovs = _.split(val, ',')
            for (var i = 0; i < ovs.length; i++) {
                // optionValuesList can be e.g. [r_1] for SELECT_ONE, or [null, r_1] for SELECT_ANY
                if (question.response && _.indexOf(question.response.optionValuesList, ovs[i]) != -1) {
                    return true
                }
            }
            return false
        },

        isAnswerableQuestion (q) {
            var is = q.type != 'INSTRUCTIONS' && this.isApplicableQuestion(q)
            return is
        },
        getNextIncompleteQuestionIndex () {
            return _.findIndex(this.questions, (q) => {
                return q.index > this.survey.current_question &&
                    this.isApplicableQuestion(q) &&
                    q.type != 'INSTRUCTIONS' &&
                    !this.isCompleted(q)
            })
        },
        getNextApplicableQuestionIndex () {
            return _.findIndex(this.questions, (q) => {
                return q.index > this.survey.current_question &&
                    this.isApplicableQuestion(q)
            })
        },
        getPreviousApplicableQuestionIndex () {
            return _.findLastIndex(this.questions, (q) => {
                return q.index < this.survey.current_question &&
                    this.isApplicableQuestion(q)
            })
        },
        getSectionBadgeVariant (section) {
            return (section == this.section ? 'primary' : 'default')
        },
    }
}
